<script setup>
import { ref, defineProps, defineEmits, onMounted } from 'vue'

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'

const props = defineProps(['mapInfo', 'fields'])
const mapInfo = ref(props.mapInfo)
const btnDisabled = ref(true)

const date = ref();
const markers = ref();
const highlights = ref();

const emit = defineEmits(['onChangeMapInfo'])

onMounted(() => {
  date.value = new Date().toLocaleDateString('sv-SE').toString().substring(0, 10);

  // 日付ピッカーに撮影日の情報をセット
  let marks = [];
  props.fields.forEach(function(field){
    field.ShootingDays.forEach(function(sw){
      sw.Days.forEach(function(day){
        const targetday = new Date(day.toString().substring(0, 4) + "-" + day.toString().substring(4, 6) + "-" + day.toString().substring(6, 8));

        let marker = marks.find(m => m.date.toString() === targetday.toString());
        if (marker) {
          marker.tooltip.push({
            text: field.id + "-" + sw.id, 
            color: field.PolygonStrokeColor
          })
          marks = marks.filter(m => m.date.toString() !== targetday.toString());
          marks.push({
            date: marker.date,
            type: "line",
            tooltip: marker.tooltip
          })
        }
        else {
          marks.push({
            date: new Date(day.toString().substring(0, 4) + "-" + day.toString().substring(4, 6) + "-" + day.toString().substring(6, 8)),
            type: "dot",
            tooltip: [{ 
              text: field.id + "-" + sw.id, 
              color: field.PolygonStrokeColor
            }],
          });
        }
      });
    });
  });
  markers.value = marks;

  // highlights.value = ([]);

})

// S/W種別変更イベント
function onChangeMapDataSW(event) {
  mapInfo.value.mapDataSW = event.target.value;
  btnDisabled.value = event.target.value === "Off" ? true : false;
  emit('onChangeMapInfo');
}

// 画像種別変更イベント
function onChangeMapType(event) {
  mapInfo.value.mapType = event.target.value;
  emit('onChangeMapInfo');
}

// 日付変更イベント
function onChangeDate (date) {
  mapInfo.value.selectedDate = date.toLocaleDateString('sv-SE').replaceAll("-", "").substring(0, 8);
  emit('onChangeMapInfo');
}

function onChangeMapLayer() {
  emit('onChangeMapInfo');
}

function openWindow() {
  window.open('https://gbank.gsj.jp/seamless/v2/legendSub.html?group=0', '_blank', 'width=1000,height=580,location=no');
}

function getDayClass(date) {
  const weekDay = new Date(date).getDay();
  if (weekDay == 6) {
    // 土曜日の場合、classに"saturday"を追加
    return 'saturday';
  }
  if (weekDay == 0) {
    // 日曜日の場合、classに"sunday"を追加
    return 'sunday';
  }
  return '';
}
</script>

<template>

  <div class="card m-0 mb-1">
    <div class="card-header py-0 bg-dark-subtle text-center">Drone Image Layer</div>
    <div class="card-body p-1">
      <div class="btn-group btn-group-sm p-1 w-100 d-flex flex-row" role="group" aria-label="Basic radio toggle button group">
        <input type="radio" class="btn-check" name="btnRadioSW" id="btnOff" value="Off" @change="onChangeMapDataSW($event)" checked>
        <label class="btn btn-outline-danger" for="btnOff">Off</label>

        <input type="radio" class="btn-check" name="btnRadioSW" id="btnDji" value="DJI" @change="onChangeMapDataSW($event)">
        <label class="btn btn-outline-danger" for="btnDji">DJI-Terra</label>

        <input type="radio" class="btn-check" name="btnRadioSW" id="btnMetashape" value="Metashape" @change="onChangeMapDataSW($event)">
        <label class="btn btn-outline-danger" for="btnMetashape">Metashape</label>
      </div>

      <div class="btn-group btn-group-sm p-1 w-100 d-flex flex-row" role="group" aria-label="Basic radio toggle button group">
        <input type="radio" class="btn-check" name="btnRadio" id="btnRgb" value="RGB" @change="onChangeMapType($event)" checked v-bind:disabled="btnDisabled">
        <label class="btn btn-outline-primary" for="btnRgb">RGB</label>

        <input type="radio" class="btn-check" name="btnRadio" id="btnNdvi" value="NDVI" @change="onChangeMapType($event)" v-bind:disabled="btnDisabled">
        <label class="btn btn-outline-primary" for="btnNdvi" data-bs-placement="bottom" 
          title="この指標は、植生のクロロフィル(葉緑素)含有量に対して最も一般的に使用される指標であり、植生の初期～中期における成長と栄養素に関する情報を提供します。この指標を使用して植生の成長状況と範囲を監視することが出来ます。">
          NDVI
        </label>

        <input type="radio" class="btn-check" name="btnRadio" id="btnGndvi" value="GNDVI" @change="onChangeMapType($event)" v-bind:disabled="btnDisabled">
        <label class="btn btn-outline-primary" for="btnGndvi" data-bs-placement="bottom"
          title="この指標は、NDVIで使用される赤色バンドの代わりに緑色バンドを使用します。緑色バンドは表面のGNDVIを表し、植生が水不足、栄養不足、または成長後のバイオマス不足に苦しんでいるかどうかを示すことが出来ます。">
          GNDVI
        </label>

        <input type="radio" class="btn-check" name="btnRadio" id="btnLci" value="LCI" @change="onChangeMapType($event)" v-bind:disabled="btnDisabled">
        <label class="btn btn-outline-primary" for="btnLci" data-bs-placement="bottom"
          title="この指標は葉のクロロフィル含有量であり、植生の成長と収穫量を評価するための重要な指標です。同時に、植物の栄養ストレス、病気、および老化を評価するための指標でもあります。">
          LCI
        </label>
        
        <input type="radio" class="btn-check" name="btnRadio" id="btnNdre" value="NDRE" @change="onChangeMapType($event)" v-bind:disabled="btnDisabled">
        <label class="btn btn-outline-primary" for="btnNdre" data-bs-placement="bottom"
          title="この指標は、NDVIで使用されている赤色エッジバンドに代わる赤色エッジバンドが使用されます。この赤色エッジバンドは、赤色スペクトルから近赤外スペクトルへの遷移区域のスペクトル領域です。NDREは、クロロフィルや糖度などの変数に基づいて植生の後期における健康を管理するために使用されます。">
          NDRE
        </label>

        <input type="radio" class="btn-check" name="btnRadio" id="btnOsavi" value="OSAVI" @change="onChangeMapType($event)" v-bind:disabled="btnDisabled">
        <label class="btn btn-outline-primary" for="btnOsavi" data-bs-placement="bottom"
          title="この指標は土壌調整植生指数であり、NDVIおよび関連する観測データに基づいています。この指標は、植生に対する土壌状態の影響を排除するために使用されます。">
          OSAVI
        </label>
      </div>

      <div class="date-picker p-1">
        <VueDatePicker
          v-model="date"
          v-bind:disabled="btnDisabled"
          format="yyyy/MM/dd"
          locale="ja"
          model-type="yyyy-MM-dd"
          week-start="0"
          :enable-time-picker="false"
          :clearable="false"
          :day-class="getDayClass"
          :highlight="highlights"
          :markers="markers"
          :max-date="new Date()"
          auto-apply
          year-first
          calendar-class-name="dp-custom-calendar"
          @date-update="onChangeDate"
        />
      </div>
    </div>
  </div>

  <div class="card m-0 mb-1">
    <div class="card-header py-0 bg-dark-subtle text-center">Weather Layer</div>
    <div class="card-body p-0">
      <table class="table table-sm table-bordered text-center m-0">
        <tbody>
          <tr class="table-light">
            <td class="p-0"></td>
            <td class="p-0"><a href="https://openweathermap.org/" target="_blank">OpenWeather</a></td>
            <td class="p-0"><a href="https://tomorrow.io/" target="_blank">Tomorrow</a></td>
          </tr>
          <tr>
            <td class="table-light p-0">雲</td>
            <td class="p-0 ps-1">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="chkOpenWeather1" v-model="mapInfo.openWeatherLayer.cloud" @change="onChangeMapLayer($event)">
              </div>
            </td>
            <td class="p-0 ps-1">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="chkTomorrw1" v-model="mapInfo.tomorrowLayer.cloudCover" @change="onChangeMapLayer($event)">
              </div>
            </td>
          </tr>
          <tr>
            <td class="table-light p-0">降水</td>
            <td class="p-0 ps-1">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="chkOpenWeather2" v-model="mapInfo.openWeatherLayer.precipitation" @change="onChangeMapLayer($event)">
              </div>
            </td>
            <td class="p-0 ps-1">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="chkTomorrw2" v-model="mapInfo.tomorrowLayer.precipitationIntensity" @change="onChangeMapLayer($event)">
              </div>
            </td>
          </tr>
          <tr>
            <td class="table-light p-0">気温</td>
            <td class="p-0 ps-1">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="chkOpenWeather3" v-model="mapInfo.openWeatherLayer.temp" @change="onChangeMapLayer($event)">
              </div>
            </td>
            <td class="p-0 ps-1">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="chkTomorrw3" v-model="mapInfo.tomorrowLayer.temperature" @change="onChangeMapLayer($event)">
              </div>
            </td>
          </tr>
          <tr>
            <td class="table-light p-0">風速</td>
            <td class="p-0 ps-1">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="chkOpenWeather4" v-model="mapInfo.openWeatherLayer.wind" @change="onChangeMapLayer($event)">
              </div>
            </td>
            <td class="p-0 ps-1">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="chkTomorrw4" v-model="mapInfo.tomorrowLayer.windSpeed" @change="onChangeMapLayer($event)">
              </div>
            </td>
          </tr>
          <tr>
            <td class="table-light p-0">気圧</td>
            <td class="p-0 ps-1">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="chkOpenWeather5" v-model="mapInfo.openWeatherLayer.pressure" @change="onChangeMapLayer($event)">
              </div>
            </td>
            <td class="p-0 ps-1">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="chkTomorrw5" v-model="mapInfo.tomorrowLayer.pressureSeaLevel" @change="onChangeMapLayer($event)">
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- 日本シームレス地質図レイヤー切り替えボタン -->
  <div class="card m-0">
    <div class="card-header py-0 ps-2 bg-dark-subtle text-center">
      <a href="#" @click="openWindow()">日本シームレス地質図 Layer</a>
    </div>
    <div class="card-body p-0">
      <div class="form-check form-switch border rounded m-2">
        <input class="form-check-input" type="checkbox" role="switch" id="chkSeamless" v-model="mapInfo.seamlessMap" @change="onChangeMapLayer($event)">
        <label class="form-check-label" for="chkSeamless">※表示可能なZoomレベル：～13？</label>
      </div>
    </div>
  </div>

</template>

<style scoped>
</style>

<style>
/* .dp-custom-calendar {
  .dp__calendar_row {
    padding: 0;
    margin: 0;
  }
  .dp__calendar_item {
    border: 1px solid var(--dp-border-color-hover);
  }
} */
.saturday {
  color: #0000ff;
}
.sunday {
  color: #ff0000;
}
</style>
